import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Gift, Calendar, Percent, AlertCircle, Sparkles } from 'lucide-react';
import { promotionApiService } from '../services/promotionApi';

interface UserPromo {
  id: number;
  code: string;
  discount_value: number;
  discount_type: string;
  start_date: string;
  end_date: string;
  status: string;
  source: string;
}

const MesPromosPage: React.FC = () => {
  const navigate = useNavigate();
  const [promos, setPromos] = useState<UserPromo[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadPromos();
  }, []);

  const loadPromos = async () => {
    try {
      const userPromos = await promotionApiService.getUserPromoCodes();
      
      // Filtrer les codes encore valides et mapper vers notre type
      const validCodes = userPromos
        .filter((promo: any) => {
          if (promo.end_date) {
            const endDate = new Date(promo.end_date);
            const now = new Date();
            return endDate > now && promo.status === 'active';
          }
          return promo.status === 'active';
        })
        .map((promo: any) => ({
          id: promo.id,
          code: promo.code,
          discount_value: promo.discount_value,
          discount_type: promo.discount_type,
          start_date: promo.start_date,
          end_date: promo.end_date,
          status: promo.status,
          source: promo.source || 'manual'
        }));

      setPromos(validCodes);
    } catch (error) {
      console.error('Erreur lors du chargement des promos:', error);
      setPromos([]);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  };

  const getDaysRemaining = (endDate: string) => {
    const end = new Date(endDate);
    const now = new Date();
    const diffTime = end.getTime() - now.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-indigo-50/20 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Chargement de vos promos...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-indigo-50/20">
      {/* Header */}
      <div className="bg-white/80 backdrop-blur-sm border-b border-gray-200 sticky top-0 z-40">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => navigate('/')}
              className="p-2 rounded-lg bg-gradient-to-r from-blue-50 to-purple-50 hover:from-blue-100 hover:to-purple-100 transition-all duration-300 border border-blue-200/50"
            >
              <ArrowLeft className="w-5 h-5 text-blue-600" />
            </button>

            <div>
              <h1 className="text-3xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600">
                Mes Promotions
              </h1>
              <p className="text-sm text-gray-600">Gérez vos codes promo et réductions</p>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {promos.length > 0 ? (
          <div className="space-y-6">
            {/* Stats */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm">
                <div className="flex items-center space-x-3">
                  <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                    <Gift className="w-6 h-6 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-gray-900">{promos.length}</p>
                    <p className="text-sm text-gray-600">Promo{promos.length > 1 ? 's' : ''} active{promos.length > 1 ? 's' : ''}</p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm">
                <div className="flex items-center space-x-3">
                  <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                    <Percent className="w-6 h-6 text-green-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-gray-900">
                      {Math.max(...promos.map(p => p.discount_value))}%
                    </p>
                    <p className="text-sm text-gray-600">Meilleure réduction</p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm">
                <div className="flex items-center space-x-3">
                  <div className="w-12 h-12 bg-amber-100 rounded-lg flex items-center justify-center">
                    <Sparkles className="w-6 h-6 text-amber-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-gray-900">
                      {promos.filter(p => p.source === 'wheel').length}
                    </p>
                    <p className="text-sm text-gray-600">Gagnées à la roue</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Liste des promos */}
            <div className="space-y-4">
              {promos.map((promo) => {
                const daysRemaining = getDaysRemaining(promo.end_date);
                const isExpiringSoon = daysRemaining <= 7;

                return (
                  <div
                    key={promo.id}
                    className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden hover:shadow-md transition-shadow"
                  >
                    <div className="p-6">
                      <div className="flex items-start justify-between">
                        <div className="flex items-start space-x-4 flex-1">
                          <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl flex items-center justify-center flex-shrink-0">
                            <Gift className="w-8 h-8 text-white" />
                          </div>

                          <div className="flex-1">
                            <div className="flex items-center space-x-3 mb-2">
                              <h3 className="text-xl font-bold text-gray-900">{promo.code}</h3>
                              <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm font-semibold">
                                -{promo.discount_value}%
                              </span>
                              {promo.source === 'wheel' && (
                                <span className="px-2 py-1 bg-amber-100 text-amber-700 rounded-full text-xs font-medium">
                                  🎡 Roue
                                </span>
                              )}
                            </div>

                            <div className="flex items-center space-x-4 text-sm text-gray-600">
                              <div className="flex items-center space-x-1">
                                <Calendar className="w-4 h-4" />
                                <span>Expire le {formatDate(promo.end_date)}</span>
                              </div>
                              {isExpiringSoon && (
                                <div className="flex items-center space-x-1 text-orange-600">
                                  <AlertCircle className="w-4 h-4" />
                                  <span className="font-medium">{daysRemaining} jour{daysRemaining > 1 ? 's' : ''} restant{daysRemaining > 1 ? 's' : ''}</span>
                                </div>
                              )}
                            </div>

                            <button
                              onClick={() => navigate('/devisrequest')}
                              className="mt-4 px-4 py-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg hover:shadow-lg transition-all text-sm font-medium"
                            >
                              Utiliser maintenant
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ) : (
          // Pas de promos
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12">
            <div className="text-center max-w-md mx-auto">
              <div className="w-24 h-24 bg-gradient-to-br from-gray-100 to-gray-200 rounded-full flex items-center justify-center mx-auto mb-6">
                <Gift className="w-12 h-12 text-gray-400" />
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-3">
                Aucune promotion active
              </h2>
              <p className="text-gray-600 mb-8">
                Vous n'avez pas encore de codes promo. Tentez votre chance à la roue de la fortune pour gagner des réductions exclusives !
              </p>

              <button
                onClick={() => {
                  navigate('/');
                  // La roue s'ouvrira automatiquement sur la page d'accueil
                }}
                className="inline-flex items-center space-x-2 px-6 py-3 bg-gradient-to-r from-amber-500 to-orange-600 text-white rounded-xl font-semibold hover:shadow-lg transition-all"
              >
                <Sparkles className="w-5 h-5" />
                <span>Jouer à la roue</span>
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MesPromosPage;
